import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X } from "lucide-react";

/**
 * Shared "Are you sure you want to leave the game?" modal.
 * Used by every game mode (Casual 1v1 + 4v4 Multiplayer) when the user:
 *   • clicks the Home / Back button
 *   • tries to close the tab / navigate away
 *   • clicks another menu item mid-match
 */
export default function LeaveConfirmModal({ open, onCancel, onConfirm, title, body }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[130] flex items-center justify-center bg-[#02040D]/70 backdrop-blur-md p-4"
          data-testid="leave-confirm-modal"
        >
          <motion.div
            initial={{ scale: 0.92, y: 18, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.92, y: 18, opacity: 0 }}
            transition={{ type: "spring", stiffness: 240, damping: 22 }}
            className="sm-card relative w-full max-w-md p-7"
            style={{ borderColor: "rgba(244,63,94,0.55)", boxShadow: "0 0 40px rgba(244,63,94,0.35)" }}
          >
            <button
              onClick={onCancel}
              className="absolute top-3 right-3 text-faint hover:text-white p-1"
              aria-label="Cancel"
              data-testid="leave-cancel-x"
            >
              <X size={18} />
            </button>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 grid place-items-center rounded-xl border border-rose-500/50 bg-rose-500/10 shrink-0">
                <AlertTriangle size={22} className="text-loss" />
              </div>
              <div className="flex-1">
                <div className="font-mono text-[10px] tracking-[0.3em] text-loss uppercase mb-1">
                  Leave Match?
                </div>
                <div className="font-heading font-black text-xl text-white">
                  {title || "Are you sure you want to leave the game?"}
                </div>
                <div className="text-soft text-sm mt-2">
                  {body || "Leaving mid-match may count as a forfeit and award the round/win to the opponent."}
                </div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <button
                onClick={onCancel}
                className="sm-btn-outline px-5 py-3"
                data-testid="leave-confirm-cancel"
              >
                CANCEL
              </button>
              <button
                onClick={onConfirm}
                className="px-5 py-3 rounded-xl font-heading font-bold tracking-[0.08em] uppercase text-white"
                style={{
                  backgroundImage: "linear-gradient(135deg, #F43F5E 0%, #BE123C 100%)",
                  boxShadow: "0 0 24px rgba(244,63,94,0.45)",
                }}
                data-testid="leave-confirm-yes"
              >
                YES, LEAVE
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
